import { memo, useContext, useState } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import { RxArrowTopRight } from "react-icons/rx";
import { NavLink, useLocation } from "react-router-dom";
import AgrogridBrandLogo from "../../assets/images/agrogrid-brand.png";
import { LiveMarketTicker } from "./live-market-ticker.component";
import { HomeNavWrapper } from "./navigation.styles";
import { ContactUsContext } from "../../providers/contactUs.provider";
import { Button } from "../Button";
import { Mixpanel } from "../../utils/mixpanel";

const landingLinks = [
  { label: "Our Farms", to: "/farms" },
  { label: "Split Buy", to: "/split-buy" },
  { label: "Market Prices", to: "/market-prices" },
  { label: "Blog", to: "/blog" },
  { label: "About Us", to: "/about-us" },
];

export const HomeNavigationComponent = memo(() => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { closeContactModal, isOpen, openContactModal } = useContext(ContactUsContext);
  const closeMenu = () => setIsMenuOpen(false);
  const toggleContactModal = () => isOpen ? closeContactModal() : openContactModal();

  const renderLink = ({ href, label, to }) => href
    ? <a href={href} onClick={closeMenu}>{label}</a>
    : <NavLink to={to} onClick={closeMenu}>{label}</NavLink>;

  const renderMobileLink = ({ href, label, to }) => {
    const content = <>{label}<RxArrowTopRight aria-hidden="true" /></>;
    return href
      ? <a className="mobile-nav-link" href={href} onClick={closeMenu}>{content}</a>
      : <NavLink className="mobile-nav-link" to={to} onClick={closeMenu}>{content}</NavLink>;
  };

  return (
    <HomeNavWrapper className={`landing-home-navigation${location.pathname.startsWith("/market-prices") ? " market-prices-navigation" : ""}`}>
      <LiveMarketTicker />
      <nav className="landing-navbar">
        <NavLink to="/" className="landing-brand" aria-label="Agrogrid home"><img src={AgrogridBrandLogo} alt="Agrogrid" /></NavLink>
        <div className="landing-nav-links">{landingLinks.map((link) => <span onClick={() => {
          if (link.label.toLowerCase() == "about us") {
            Mixpanel.track(`Tap-HomeScreen-AboutUsNavMenu`)
          }
        }} key={link.label}>{renderLink(link)}</span>)}</div>
        <div className="landing-nav-actions">
          <NavLink onClick={() => {
            Mixpanel.track("Tap-HomeScreen-BusinessesNavMenu")
          }} to="/businesses">Businesses <RxArrowTopRight aria-hidden="true" /></NavLink>
          <NavLink onClick={() => {
            Mixpanel.track("Tap-HomeScreen-HouseHoldNavMenu")
          }} to="/split-buy">Household <RxArrowTopRight aria-hidden="true" /></NavLink>
        </div>
        <button type="button" className="landing-menu-toggle" onClick={() => setIsMenuOpen((current) => !current)} aria-expanded={isMenuOpen} aria-label="Toggle navigation menu">{isMenuOpen ? <FiX /> : <FiMenu />}</button>
        <AnimatePresence>
          {isMenuOpen && <motion.div className="landing-mobile-menu menu_links" initial={{ top: 0, opacity: .3 }} animate={{ top: "120%", opacity: 1 }} exit={{ top: "-120%", opacity: 0 }}>
            <div className="mobile-menu-links">{landingLinks.map((link) => <span onClick={() => {
              if (link.label.toLowerCase() == "about us") {
                Mixpanel.track(`Tap-HomeScreen-AboutUsNavMenu`)
              }
            }} key={link.label}>{renderMobileLink(link)}</span>)}</div>
            <div className="mobile-action-links">
              <NavLink className="mobile-business-link" to="/businesses" onClick={() => {
                Mixpanel.track("Tap-HomeScreen-BusinessesNavMenu")
                closeMenu()
              }}>Businesses <RxArrowTopRight aria-hidden="true" /></NavLink>
              <NavLink className="mobile-household-link" to="/split-buy" onClick={() => {
                Mixpanel.track("Tap-HomeScreen-HouseHoldNavMenu")
                closeMenu()
              }}>Household <RxArrowTopRight aria-hidden="true" /></NavLink>
            </div>
          </motion.div>}
        </AnimatePresence>
      </nav>
      <div className="support-menu">
        <Button onClick={toggleContactModal} className="support" aria-label="Contact support">
          <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.625 13.5C4.43153 13.5 3.28693 13.9741 2.44302 14.818C1.59911 15.6619 1.125 16.8065 1.125 18V22.5C1.125 23.6935 1.59911 24.8381 2.44302 25.682C3.28693 26.5259 4.43153 27 5.625 27H6.75C7.04837 27 7.33452 26.8815 7.5455 26.6705C7.75647 26.4595 7.875 26.1734 7.875 25.875V14.625C7.875 14.3266 7.75647 14.0405 7.5455 13.8295C7.33452 13.6185 7.04837 13.5 6.75 13.5H5.625ZM5.625 13.5C5.625 10.2179 6.92879 7.07032 9.24955 4.74955C11.5703 2.42879 14.7179 1.125 18 1.125C21.2821 1.125 24.4297 2.42879 26.7504 4.74955C29.0712 7.07032 30.375 10.2179 30.375 13.5M30.375 27H29.25C28.9516 27 28.6655 26.8815 28.4545 26.6705C28.2435 26.4595 28.125 26.1734 28.125 25.875V14.625C28.125 14.3266 28.2435 14.0405 28.4545 13.8295C28.6655 13.6185 28.9516 13.5 29.25 13.5H30.375M30.375 27C31.5685 27 32.7131 26.5259 33.557 25.682C34.4009 24.8381 34.875 23.6935 34.875 22.5V18C34.875 16.8065 34.4009 15.6619 33.557 14.818C32.7131 13.9741 31.5685 13.5 30.375 13.5M30.375 27V28.125C30.375 29.3185 29.9009 30.4631 29.057 31.307C28.2131 32.1509 27.0685 32.625 25.875 32.625H22.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13.5 12.3749C13.5 11.34 13.8568 10.3367 14.5101 9.53407C15.1635 8.73145 16.0736 8.17857 17.087 7.9686C18.1004 7.75862 19.1552 7.90438 20.0737 8.38131C20.9922 8.85824 21.7183 9.6372 22.1295 10.5869C22.4565 11.3444 22.5705 12.1769 22.4565 12.9944C22.3432 13.8114 22.0074 14.5816 21.486 15.2207C20.9646 15.8598 20.2776 16.3433 19.5 16.6184C19.0611 16.7736 18.6812 17.0611 18.4125 17.4412C18.1439 17.8213 17.9998 18.2754 18 18.7409V19.1249M20.25 34.8749H18C17.4033 34.8749 16.831 34.6379 16.409 34.2159C15.9871 33.794 15.75 33.2217 15.75 32.6249C15.75 32.0282 15.9871 31.4559 16.409 31.0339C16.831 30.612 17.4033 30.3749 18 30.3749H20.25C20.8467 30.3749 21.419 30.612 21.841 31.0339C22.2629 31.4559 22.5 32.0282 22.5 32.6249C22.5 33.2217 22.2629 33.794 21.841 34.2159C21.419 34.6379 20.8467 34.8749 20.25 34.8749Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 24.75C17.8508 24.75 17.7077 24.6907 17.6023 24.5852C17.4968 24.4798 17.4375 24.3367 17.4375 24.1875C17.4375 24.0383 17.4968 23.8952 17.6023 23.7898C17.7077 23.6843 17.8508 23.625 18 23.625M18 24.75C18.1492 24.75 18.2923 24.6907 18.3977 24.5852C18.5032 24.4798 18.5625 24.3367 18.5625 24.1875C18.5625 24.0383 18.5032 23.8952 18.3977 23.7898C18.2923 23.6843 18.1492 23.625 18 23.625" stroke="white" strokeWidth="1.5" />
          </svg>
        </Button>
      </div>
    </HomeNavWrapper>
  );
});
